import { Route } from '@angular/router';
import { authGuard, nonAuthGuard, adminGuard } from '@rapider/authentication';
import { activeProjectGuard } from '@rapider/workspaces';

import { provideAiChatFeature } from '@rapider/ai-chat';
import { provideProjectDataFeature } from '@rapider/project-data';
import { provideDatabaseFeature } from '@rapider/database';
import { provideDataSourceFeature } from '@rapider/data-source';
import { provideWorkitemsFeature } from '@rapider/workitems';

export const appRoutes: Route[] = [
  /* =========================================
     PUBLIC AUTH ROUTES (No Layout Wrapper)
     Protected by nonAuthGuard: Logged-in users cannot access these.
     ========================================= */
  {
    path: 'auth/login',
    title: 'Login - Rapider AI',
    canActivate: [nonAuthGuard],
    loadComponent: () =>
      import('@rapider/authentication').then((c) => c.LoginComponent),
  },
  {
    path: 'auth/register',
    title: 'Register - Rapider AI',
    canActivate: [nonAuthGuard],
    loadComponent: () =>
      import('@rapider/authentication').then((c) => c.RegisterComponent),
  },
  {
    path: 'auth/forgot-password',
    title: 'Forgot Password - Rapider AI',
    canActivate: [nonAuthGuard],
    loadComponent: () =>
      import('@rapider/authentication').then((c) => c.ForgotPasswordComponent),
  },
  {
    path: 'auth/social-media-login',
    title: 'Authenticating - Rapider AI',
    loadComponent: () =>
      import('@rapider/authentication').then(
        (c) => c.SocialMediaLoginComponent,
      ),
  },
  {
    path: 'auth/person-invitation/accept',
    title: 'Accept Workspace Invitation - Rapider AI',
    loadComponent: () =>
      import('@rapider/authentication').then(
        (c) => c.AcceptInvitationComponent,
      ),
  },

  /* =========================================
     AUTHENTICATED ROUTES (Wrapped in Layout Shell)
     Protected by authGuard: Only logged-in users can access these.
     ========================================= */
  {
    path: '',
    canActivate: [authGuard, activeProjectGuard],
    // Providers for the entire authenticated section - includes the AI Chat NgRx state/effects
    providers: [
      provideAiChatFeature(),
      provideProjectDataFeature(),
      provideDatabaseFeature(),
      provideDataSourceFeature(),
      provideWorkitemsFeature(),
    ],
    loadComponent: () =>
      import('@rapider/authenticated-layout').then(
        (c) => c.AuthenticatedLayoutComponent,
      ),
    children: [
      {
        path: 'chat',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        title: 'Dashboard - Rapider AI',
        loadComponent: () =>
          import('@rapider/main-dashboard').then(
            (m) => m.MainDashboardComponent,
          ),
      },
      {
        path: 'inbox',
        title: 'Inbox - Rapider AI',
        loadComponent: () =>
          import('@rapider/workitems').then(
            (m) => m.InboxComponent || m.HomeComponent,
          ),
      },
      {
        path: 'home',
        title: 'Home - Rapider AI',
        data: { collapseSidebar: false },
        loadComponent: () =>
          import('@rapider/home-screen').then((m) => m.HomeScreenComponent),
      },
      {
        path: 'data-operations-log',
        title: 'Data Operations Log - Rapider AI',
        loadComponent: () =>
          import('@rapider/database').then(
            (m) => m.DataInsightTimelineComponent,
          ),
      },

      {
        path: 'data-insight',
        title: 'Data Insight - Rapider AI',
        loadComponent: () =>
          import('@rapider/database').then((m) => m.DataInsightComponent),
      },

      /* =========================================================
         APP STORE ROUTES
         ========================================================= */
      {
        path: 'applications',
        title: 'Apps - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-store').then(
            (m) => m.InstalledApplicationsComponent,
          ),
      },
      {
        path: 'application-settings',
        title: 'Application Settings - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-store').then(
            (m) => m.ApplicationSettingsComponent,
          ),
      },
      {
        path: 'app-store',
        title: 'App Store - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-store').then((m) => m.AppStoreListComponent),
      },
      {
        path: 'app-store/:appId',
        title: 'App Details - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-store').then((m) => m.AppStoreDetailComponent),
      },

      /* =========================================================
         APP IDEAS ROUTES
         ========================================================= */
      {
        path: 'app-ideas',
        title: 'App Factory - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-ideas').then((m) => m.AppIdeasListComponent),
      },
      {
        path: 'app-ideas/:id',
        title: 'App Factory Details - Rapider AI',
        loadComponent: () =>
          import('@rapider/app-ideas').then((m) => m.AppIdeaDetailComponent),
      },

      /* =========================================================
         AI FUNCTION CALLS ROUTES
         ========================================================= */
      {
        path: 'ai-function-calls',
        title: 'AI Functions - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/ai-function-calls').then(
            (m) => m.AiFunctionCallsListComponent,
          ),
      },
      {
        path: 'ai-function-calls/detail/:id',
        title: 'AI Function Details - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/ai-function-calls').then(
            (m) => m.AiFunctionCallDetailComponent,
          ),
      },

      /* =========================================================
         LAMBDA FUNCTIONS ROUTES
         ========================================================= */
      {
        path: 'lambda-functions',
        data: {},
        loadChildren: () =>
          import('@rapider/lambda-functions').then(
            (m) => m.lambdaFunctionsRoutes,
          ),
      },

      /* =========================================================
         FUNCTIONS ROUTES
         ========================================================= */
      {
        path: 'functions',
        title: 'Functions - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/functions').then((m) => m.FunctionsComponent),
      },

      /* =========================================================
         WORKFLOWS ROUTES
         ========================================================= */
      {
        path: 'workflows',
        title: 'Workflows - Rapider AI',
        // data: {},
        loadChildren: () =>
          import('@rapider/workflow').then((m) => m.workflowRoutes),
      },

      /* =========================================================
         KNOWLEDGE BASE ROUTES
         ========================================================= */
      {
        path: 'data-sources',
        title: 'Data Sources - Rapider AI',
        loadChildren: () =>
          import('@rapider/data-source').then((m) => m.dataSourceRoutes),
      },

      /* =========================================================
         WORKSPACE FLOW ROUTES
         ========================================================= */
      {
        path: 'definitions',
        title: 'Project Definitions - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/project-definitions').then(
            (m) => m.DefinitionsListComponent,
          ),
      },
      {
        path: 'definitions/:id',
        title: 'Edit Definition - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/project-definitions').then(
            (m) => m.DefinitionEditComponent,
          ),
      },
      {
        path: 'select-workspace',
        title: 'Select Workspace - Rapider AI',
        data: {
          viewMode: 'canvas-only-not-resizable',
          hideSidebar: true,
        },
        loadComponent: () =>
          import('@rapider/workspaces').then((m) => m.WorkspaceSelectComponent),
      },
      {
        path: 'create-workspace',
        title: 'Create Workspace - Rapider AI',
        data: {
          viewMode: 'canvas-only-not-resizable',
          hideSidebar: true,
        },
        loadComponent: () =>
          import('@rapider/workspaces').then((m) => m.WorkspaceCreateComponent),
      },
      {
        path: 'workspace-settings',
        title: 'Workspace Settings - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/workspaces').then(
            (m) => m.WorkspaceSettingsComponent,
          ),
      },
      {
        path: 'profile',
        title: 'Profile Settings - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/authentication').then((m) => m.ProfileComponent),
      },
      {
        path: 'shortcuts',
        title: 'Keyboard Shortcuts - Rapider AI',
        data: { viewMode: 'split' },
        loadComponent: () =>
          import('@rapider/authenticated-layout').then(
            (m) => m.KeyboardShortcutsPageComponent,
          ),
      },

      /* =========================================================
         ONBOARDING / SETUP ROUTES
         ========================================================= */
      {
        path: 'onboarding',
        title: 'Onboarding - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.OnboardingComponent),
      },
      {
        path: 'data-templates',
        title: 'Data Templates - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.DataTemplatesComponent),
      },
      {
        path: 'data-templates/:id',
        title: 'Template Details - Rapider AI',
        loadComponent: () =>
          import('@rapider/onboarding').then(
            (m) => m.DataTemplatesPreviewComponent,
          ),
        data: { viewMode: 'canvas-only-not-resizable' },
      },
      {
        path: 'data-templates/:id/integrate',
        title: 'Integrate Template - Rapider AI',
        loadComponent: () =>
          import('@rapider/onboarding').then(
            (m) => m.ProjectModelLibraryIntegrationComponent,
          ),
        data: { viewMode: 'canvas-only-not-resizable' },
      },
      {
        path: 'import-data',
        title: 'Import Data - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.ImportDataComponent),
      },
      {
        path: 'ai-setup',
        title: 'Start with AI - Rapider AI',
        data: { viewMode: 'canvas-only-not-resizable' },
        loadComponent: () =>
          import('@rapider/onboarding').then((m) => m.AiSetupComponent),
      },

      /* =========================================================
         DATABASE ROUTES
         ========================================================= */
      {
        path: 'database',
        title: 'Database - Rapider AI',
        data: { collapseSidebar: false },
        loadComponent: () =>
          import('@rapider/database').then((m) => m.DatabaseListComponent),
      },
      {
        // NEW HEADLESS ROUTE ADDED HERE FOR SEMANTIC DATABASE LINKING
        path: 'navigate-to-project-model-with-name/:projectModelName',
        loadComponent: () =>
          import('@rapider/database').then(
            (m) => m.NavigateToProjectModelComponent,
          ),
      },
      {
        path: 'database/:id',
        title: 'Model Details - Rapider AI',
        loadComponent: () =>
          import('@rapider/database').then(
            (m) => m.ProjectModelDetailComponent,
          ),
      },

      /* =========================================================
         COMPANY KNOWLEDGE ROUTES (notes, pages, activity, graph)
         ========================================================= */
      {
        path: 'knowledge',
        title: 'Notebooks - Rapider AI',
        data: {},
        loadChildren: () =>
          import('@rapider/knowledge').then((m) => m.KNOWLEDGE_CHILD_ROUTES),
      },
      /* Notebooks & legacy spaces URLs → Knowledge. */
      { path: 'notebooks', redirectTo: 'knowledge/notebooks', pathMatch: 'full' },
      { path: 'notebooks/:pageKey', redirectTo: 'knowledge/notebooks/:pageKey', pathMatch: 'full' },
      { path: 'spaces', redirectTo: 'knowledge', pathMatch: 'full' },
      { path: 'spaces/save-to-page', redirectTo: 'knowledge/save-to-page', pathMatch: 'full' },
      { path: 'spaces/:spaceKey/graph', redirectTo: 'knowledge/s/:spaceKey/graph', pathMatch: 'full' },
      { path: 'spaces/:spaceKey/:pageKey', redirectTo: 'knowledge/s/:spaceKey/notebooks/:pageKey', pathMatch: 'full' },
      { path: 'spaces/:spaceKey', redirectTo: 'knowledge/s/:spaceKey/notes', pathMatch: 'full' },
      /* =========================================================
         DYNAMIC APPLICATION VIEWER ROUTE
         ========================================================= */
      {
        path: 'app/:appId',
        title: 'Application Viewer - Rapider AI',
        data: { mode: 'installed' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.DynamicAppViewerComponent,
          ),
      },
      {
        path: 'app/:appId/:pageKey',
        title: 'Application Viewer - Rapider AI',
        data: { mode: 'installed' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.DynamicAppViewerComponent,
          ),
      },
      {
        path: 'app/:appId/:pageKey/:entityId',
        title: 'Application Viewer - Rapider AI',
        data: { mode: 'installed' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.DynamicAppViewerComponent,
          ),
      },
      {
        path: 'app/:appId/:pageKey/:action/:entityId',
        title: 'Application Viewer - Rapider AI',
        data: { mode: 'installed' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.DynamicAppViewerComponent,
          ),
      },

      /* =========================================================
         WORKFLOW EXECUTION ROUTES
         ========================================================= */
      {
        path: 'workflow-execution',
        title: 'Workflow Execution - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/ai-workflow').then(
            (m) => m.UiWorkflowExecutionComponent,
          ),
      },

      /* =========================================================
         SHOWCASE ROUTES
         ========================================================= */
      {
        path: 'components-showcase',
        title: 'UI Components Showcase - Rapider AI',
        loadComponent: () =>
          import('@rapider/components-showcase').then(
            (m) => m.ComponentsShowcaseComponent,
          ),
      },

      /* =========================================================
         CONTENT EDITOR / APP BUILDER ROUTES
         ========================================================= */
      {
        path: 'app-builder',
        title: 'App Builder - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/content-editor').then((m) => m.AppBuilderComponent),
      },
      {
        path: 'app-builder/create',
        title: 'Create App - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/content-editor').then((m) => m.AppCreateComponent),
      },
      {
        path: 'app-builder/tasks',
        title: 'Task Manager - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/content-editor').then(
            (m) => m.AppTaskManagerComponent,
          ),
      },
      {
        path: 'app-builder/:appId',
        title: 'App Builder - Rapider AI',
        data: {},
        loadComponent: () =>
          import('@rapider/content-editor').then((m) => m.AppBuilderComponent),
      },
      {
        path: 'app-builder/:appId/preview',
        title: 'Preview App Harness - Rapider AI',
        data: { mode: 'builder' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.AppHarnessViewerComponent,
          ),
      },
      {
        path: 'app-builder/:appId/preview/:pageKey',
        title: 'Preview App Harness - Rapider AI',
        data: { mode: 'builder' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.AppHarnessViewerComponent,
          ),
      },
      {
        path: 'app-builder/:appId/preview/:pageKey/:entityId',
        title: 'Preview App Harness - Rapider AI',
        data: { mode: 'builder' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.AppHarnessViewerComponent,
          ),
      },
      {
        path: 'app-builder/:appId/preview/:pageKey/:action/:entityId',
        title: 'Preview App Harness - Rapider AI',
        data: { mode: 'builder' },
        loadComponent: () =>
          import('@rapider/template-renderer').then(
            (m) => m.AppHarnessViewerComponent,
          ),
      },
      {
        path: 'app-builder/:appId/page/:pageId/design-pipeline',
        title: 'AI Design Pipeline - Rapider AI',
        data: { collapseSidebar: true }, // Fullscreen editor mode
        loadComponent: () =>
          import('@rapider/content-editor').then(
            (m) => m.PageDesignPipelineComponent,
          ),
      },
      {
        path: 'app-builder/:appId/page/:pageId/ui-designer',
        data: { collapseSidebar: true }, // Fullscreen editor mode
        loadComponent: () =>
          import('@rapider/content-editor').then(
            (m) => m.DragDropBuilderComponent,
          ),
      },
      {
        path: 'app-builder/:appId/page/:pageId/review',
        title: 'Page Review - Rapider AI',
        data: { collapseSidebar: true }, // Fullscreen review mode
        loadComponent: () =>
          import('@rapider/content-editor').then(
            (m) => m.InteractivePageReviewComponent,
          ),
      },
      {
        path: 'admin',
        title: 'Administration - Rapider AI',
        canActivate: [adminGuard],
        data: {},
        loadChildren: () => import('@rapider/admin').then((m) => m.adminRoutes),
      },
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: '**',
        redirectTo: 'home',
      },
    ],
  },

  /* =========================================
     FALLBACK ROUTE
     ========================================= */
  {
    path: '**',
    redirectTo: 'auth/login',
  },
];
